import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router'
import { CIRCLE_SECTION_LABELS, type CircleSection } from '../../shared/sections'
import { Choices } from '@/components/Choice'
import { FormError } from '@/components/FormError'
import { FramilyPicker } from '@/components/FramilyPicker'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import { useFamilies } from '@/lib/families'
import { setAside, setAsideNow, type WaitingInvite } from '@/lib/invites'
import { lastFamily, opensOnCircle, usePageFrame } from '@/lib/frame'
import { useMe } from '@/lib/useMe'
import { PageLoader } from '@/components/Loader'

// What a new circle can share; the family tree is asked about on its own.
const SHARED: CircleSection[] = ['photos', 'moments', 'recipes', 'calendar']

// The app opens on Everyone once it's offered, until you pick a circle from your profile; then in the circle you were
// last in, or your first. Invites waiting for your email come first, however you
// signed in, so joining never depends on finding the emailed link. Before there's any circle or your own space, the
// first sign-in's steps show instead (Onboarding).
export function Home() {
  const families = useFamilies()
  const everyone = useMe().data?.everyone
  const invites = useQuery({
    queryKey: ['invites'],
    queryFn: () => api<{ invites: WaitingInvite[] }>('/invites'),
  })
  const [later, setLater] = useState(setAside)
  if (families.isPending || invites.isPending) return <PageLoader />
  if (families.isError) return <FormError error={families.error} />
  const list = families.data.families
  const waiting = invites.data?.invites.filter((x) => !later.includes(x.id)) ?? []
  if (list.length === 0) return null
  if (waiting.length > 0) {
    return (
      <InvitesWaiting
        invites={waiting}
        onLater={() => {
          setAsideNow(waiting.map((x) => x.id))
          setLater(setAside())
        }}
      />
    )
  }
  if (everyone && !opensOnCircle()) return <Navigate to="/everyone" replace />
  const last = lastFamily()
  const id = list.find((f) => f.id === last)?.id ?? list[0]!.id
  return <Navigate to={`/circle/${id}`} replace />
}

// Who has invited you, each opening the invite itself (accept, "Is this you?", your home). Setting them aside lasts
// until the app is next opened.
function InvitesWaiting({ invites, onLater }: { invites: WaitingInvite[]; onLater: () => void }) {
  const title = invites.length === 1 ? 'You’re invited' : 'You have invites'
  usePageFrame(undefined, { title })
  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <p className="eyebrow">Waiting for you</p>
        <h1 className="text-display">{title}</h1>
      </header>
      <ul className="grid gap-3" aria-label="Invites">
        {invites.map((x) => (
          <li key={x.id} className="flex items-center gap-3 rounded-lg border bg-card p-4">
            {x.person && <Avatar person={x.person} className="size-12 text-lg" />}
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="font-semibold tracking-tight">{x.family}</span>
              <span className="text-sub text-muted-foreground">{x.from} invited you.</span>
            </span>
            <Button asChild>
              <Link to={`/join/${x.id}`} aria-label={`Open the invite to ${x.family}`}>
                Open
              </Link>
            </Button>
          </li>
        ))}
      </ul>
      <Button variant="ghost" className="self-start" onClick={onLater}>
        Not now
      </Button>
    </section>
  )
}

export function NewFamily() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [shares, setShares] = useState<CircleSection[]>(SHARED)
  const [tree, setTree] = useState(false)
  const [bring, setBring] = useState<string[]>([])
  const [invite, setInvite] = useState(true)
  const me = useMe()
  const on = me.data?.sections
  const offered = SHARED.filter((s) => on?.[s] ?? true)
  const sections = [...shares.filter((s) => offered.includes(s)), ...(tree && on?.people ? ['people' as const] : [])]
  const title = 'Create a circle'
  usePageFrame(undefined, { title, back: { to: '/', label: 'Back' } })
  const create = useMutation({
    mutationFn: (name: string) =>
      api<{ id: string }>('/families', {
        method: 'POST',
        body: { name, sections, bring, invite },
      }),
    onSuccess: async ({ id }) => {
      await Promise.all(
        // Everyone is offered from your first circle (/me), so that's asked again too.
        ['me', 'families', 'circles', 'share-places', 'framily'].map((key) =>
          queryClient.invalidateQueries({ queryKey: [key] }),
        ),
      )
      navigate(`/circle/${id}`)
    },
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    create.mutate(name.trim())
  }

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <p className="eyebrow">Circles</p>
        <h1 className="text-display">{title}</h1>
        <p className="text-muted-foreground">
          Family, friends, a team or a club: it’s yours. Give it a name, and invite people once it’s made.
        </p>
      </header>
      <form onSubmit={submit} className="grid gap-3">
        {/* This only picks what's on to begin with; any circle can turn anything on later (Grow this circle). */}
        <Choices<CircleSection>
          legend="What will you share?"
          hint="You can add more any time."
          value={shares}
          options={offered.map((s) => [s, CIRCLE_SECTION_LABELS[s]])}
          onChange={setShares}
        />
        {on?.people && (
          <Choices<'tree'>
            legend="Family tree"
            hint="For a family: a tree to walk together, a page for each home, and memorial pages."
            value={tree ? ['tree'] : []}
            options={[['tree', 'Add a family tree']]}
            onChange={(v) => setTree(v.length > 0)}
          />
        )}
        <Label htmlFor="circle-name" className="mt-3">
          Circle name
        </Label>
        <Input id="circle-name" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
        {me.data?.sections.people && (
          <FramilyPicker value={bring} onChange={setBring} invite={invite} onInvite={setInvite} />
        )}
        <FormError error={create.error} />
        <Button type="submit" disabled={create.isPending || sections.length === 0} className="self-start">
          Create circle
        </Button>
      </form>
    </section>
  )
}
